import React, { useContext, useEffect, useMemo, useState } from "react";
import { Label, Pivot, PivotItem, Spinner, SpinnerSize } from "@fluentui/react";
import Toast from "../../components/Toast/Toast";
import "./styles/styles.css";
import AlertSubmitSection from "./components/RaiseAlertSection/AlertSubmitSection";
import RadioDropdownSection from "./components/RadioDropdownSection/RadioDropdownSection";
import CustomTabs from "./components/Tabs";
import EditorSection from "./components/EditorSection/EditorSection";
import studyReportContext from "../../context/studyreport/StudyReportContext";
import { config, circlStyle } from "./helper/initialData";
import FroalaEditor from "react-froala-wysiwyg";
import Froalaeditor from "froala-editor";
import LockWarning from "./components/LockWarning/LockWarning";
import AddTab from "./components/AddTab/AddTab";
import CustomEditor from "./components/CustomEditorSection/CustomEditor";
import RSNAModal from "./components/ReportModals/RSNAModal/RSNAModal";
import HrctChestILDModal from "./components/ReportModals/HrctChestILDModal/HrctChestILDModal";
import CTBrainModal from "./components/ReportModals/CTBrainModal/CTBrainModal";
import HrctChestCovid from "./components/ReportModals/HRCTChestCovid/HrctChestCovid";
import ReturnStudyModal from "../StudyViewer/Components/ReturnStudyModal/ReturnStudyModal";
import CustomCKEditor from "./components/CustomEditorSection/CustomCKEditor";
import userContext from "../../context/users/UserContext";
import MRISpine from "./components/ReportModals/MRSpine/MRISpine";
import AddendumDisclaimerModal from "./components/ReportModals/DisclaimerModal/AddendumDisclaimerModal";
import SubmitTick from "../StudyViewer/Components/SubmitTick/SubmitTick";
import StudyExpirationPopup from "../StudyViewer/Components/Unassignment/StudyExpirationPopup";
import ForceReinference from "./components/ReportModals/ForceReinference/ForceReinference";
import ChatInput from "./components/ChatInterface/ChatInput";
import InstructionStack from "./components/ChatInterface/InstructionStack";
import { NONXRAYMODALITIES } from "../../constants/Constants";
import { isLLMAgentPresent } from "../../service/MaskImageService";
import LLMTopBar from "./components/LLMReportHeader/LLMTopBar";
import LLMAddendumTabs from "./components/LLMReportHeader/LLMAddendumTabs";
import AgenticReportingContext from "../../context/agentic-reporting/AgenticReportingContext";
import ReportSnapshotRecoveryModal from "./components/ReportModals/ReportSnapshotRecoveryModal/ReportSnapshotRecoveryModal";

const NewReport = (props) => {
  const { userPrompts, isVapiStenographActive, isAgenticTab, isInstructionCollapsed, setIsInstructionCollapsed } =
    useContext(AgenticReportingContext);

  const {
    showToast,
    openRSNAModal,
    RSNAModalContent,
    setOpenRSNAModal,
    setReportModalState,
    reportModalState,
    currentActiveTab,
    regenerateClick,
    regenerateHRCT,
    showReturnStudyModal,
    setShowReturnStudyModal,
    showAddendumDisclaimerModal,
    setShowAddendumDisclaimerModal,
    showConfirmModal,
    setShowConfirmModal,
    isChildStudyReportState,
    setIsChildStudyReportState,
    handleUpdateReportSection,
    allReportTemplatesLoading,
    setFirmId,
    showForceReinferenceModal,
    templateHeight,
    isExternal,
    studyDescription,
    modality,
    reportDoneStatusState,
    reportingDoctorName,
    isWordFilePath,
    isWidget,
    setReportAutosaveDeps,
    snapshotRecoveryModalOpen,
    snapshotDraftPatientReports,
    snapshotOfficialPatientReports,
    handleSnapshotRecoveryAccept,
    handleSnapshotRecoveryClose,
    handleSnapshotRecoveryReject,
    activeTab,
    disableEditReport,
    studyReportPdfSelected,
    isexternalStudyReport
  } = useContext(studyReportContext);

  const {
    editorName,
    authenticated,
    loadByUsername,
    enableLlmReport,
    username,
  } = useContext(userContext);

  useEffect(() => {
    if (!authenticated) {
      loadByUsername();
    }
  }, []);

  useEffect(() => {
    if (!setReportAutosaveDeps) return;
    setReportAutosaveDeps({
      keyImages: props.keyImages,
      annotationData: props.annotationData,
      annotationDataAI: props.annotationDataAI,
      allTextToolAnnotation: props.allTextToolAnnotation,
      activeViewportIndex: props.activeViewportIndex,
    });
  }, [
    props.keyImages,
    props.annotationData,
    props.annotationDataAI,
    props.allTextToolAnnotation,
    props.activeViewportIndex,
    setReportAutosaveDeps,
  ]);

  const hideModal = () => {
    // console.log("hiding");
    setOpenRSNAModal(false);
  };

  const [chatInputHeight, setChatInputHeight] = useState(30);

  const isVerticalMonitor = window.innerWidth <= 1200;

  const isExternalLLM =
    isExternal &&
    enableLlmReport &&
    isLLMAgentPresent(props.modality, studyDescription);

  // height calculation for external reporting
  // Modal body = modal height (700/900) minus modal header (~54px)
  const MODAL_BODY_H = isVerticalMonitor ? 846 : 646;
  // Top chrome: LLMTopBar + LLMAddendumTabs ≈ 78px
  const LLM_TOP_H    = 78;
  // AlertSubmitSection ≈ 46px
  const ACTION_H = 46;
  const CHAT_COLLAPSED_H = 62; 
  const CHAT_EXPANDED_H  = 119; 

  const editorHeight = useMemo(() => {
    if (isExternalLLM) {
      // On the Addendum tab there's no chat input
      if (!isAgenticTab || activeTab !== 1 || disableEditReport) {
        return `${10 + MODAL_BODY_H - LLM_TOP_H - ACTION_H}px`; 
      }
      const chatH = isInstructionCollapsed ? CHAT_COLLAPSED_H : CHAT_EXPANDED_H; 
      return `${MODAL_BODY_H - LLM_TOP_H - chatH - ACTION_H}px`;
    }

    if (isExternal && !enableLlmReport) {
      return isVerticalMonitor ? "700px" : "478px";
    }

    if (
      enableLlmReport &&
      isLLMAgentPresent(props.modality, studyDescription) &&
      (activeTab === 2 || disableEditReport)
    ) {
      return `calc(100% - 78px)`;
    }

    if (isInstructionCollapsed && isAgenticTab && activeTab === 1 && !disableEditReport) {
      if (chatInputHeight === 36 || isVapiStenographActive)
        return `calc(100% - 144px)`;
      return `calc(100% - (${111 + chatInputHeight}px))`;
    }

    if (
      enableLlmReport &&
      isLLMAgentPresent(props.modality, studyDescription) &&
      (!isAgenticTab || disableEditReport || activeTab !== 1)
    ) {
      return `calc(100% - 78px)`;
    }

    return `calc(100% - ${
      enableLlmReport && isLLMAgentPresent(props.modality, studyDescription)
        ? 210
        : 106 + templateHeight
    }px)`;
  }, [
    isExternalLLM,
    isExternal,
    isInstructionCollapsed,
    chatInputHeight,
    isAgenticTab,
    enableLlmReport,
    props.modality,
    studyDescription,
    isVapiStenographActive,
    templateHeight,
    activeTab,
    MODAL_BODY_H,
    disableEditReport,
  ]);

  const hideModalCT = () => {
    setReportModalState(() => {
      const newVal = { ...reportModalState };
      newVal.hrctChestILD = false;
      newVal.hrctChestCovid = false;
      newVal.ctBrain = false;
      newVal.mriSpine = false;
      newVal.content = {};
      return newVal;
    });
  };

  const hideModalCTBrain = () => {
    setReportModalState(() => {
      const newVal = { ...reportModalState };
      newVal.hrctChestILD = false;
      newVal.hrctChestCovid = false;
      newVal.ctBrain = false;
      newVal.content = {};
      return newVal;
    });
  };

  const regenerateRSNA = (reportName) => {
    hideModal();
    if (reportName == "TUBERCULOSIS") {
      regenerateClick();
    }
  };

  const regenerateHRCTFunc = (reportName) => {
    // console.log("Console log"+reportModalState.content);

    hideModalCT();
    regenerateHRCT(reportModalState.content);
  };

  const toggleEditorFunc = () => {
    // setToggleEditor((prev) => !prev);
  };

  useEffect(() => {
    if (
      !props.isStudyList &&
      props.activeStudyId != null &&
      props.studyParentId != null
    ) {
      // console.log("--- Active Study Id ---",props.activeStudyId);
      if (props.activeStudyId == props.studyParentId) {
        setIsChildStudyReportState(false);
      } else {
        setIsChildStudyReportState(true);
        handleUpdateReportSection(
          props.parentChildReportsObj,
          props.activeStudyId,
        );
      }
    }
  }, [props.activeStudyId]);

  const checkForParentOrChild = () => {
    if (props.activeStudyId == null || props.studyParentId == null) {
      return false;
    }
    return props.activeStudyId != props.studyParentId;
  };

  const customSpinnerStyle = {
    root: {
      height: "100px",
    },
    circle: {
      height: "50px",
      width: "50px",
      borderWidth: "3px",
    },
  };

  return (
    <>
      <div
        className="w100 h100"
        style={{
          height: isWidget ? "605px" : (isExternalLLM ? "100%" : props.isStudyList ? "auto" : "100%"),
          width: isWidget ? "676px" : "100%",
        }}
      >
        {/* <div className="spinner-place">
        {showToast.loading &&  <Spinner size={SpinnerSize.large} styles={circlStyle}/>}
        </div> */}

        {(props.showStudyExpirationPopup ||
          props.showStudyAlreadyExpiredPopup) && (
          <StudyExpirationPopup
            setShowReturnStudyModal={setShowReturnStudyModal}
          />
        )}
        {reportModalState.hrctChestILD && (
          <HrctChestILDModal
            regenerateClick={regenerateHRCTFunc}
            hideModal={hideModalCT}
            id={reportModalState.id}
            content={reportModalState.content}
            isModalOpen={reportModalState.hrctChestILD}
          />
        )}
        {reportModalState.ctBrain && (
          <CTBrainModal
            regenerateClick={regenerateHRCTFunc}
            hideModal={hideModalCTBrain}
            id={reportModalState.id}
            content={reportModalState.content}
            isModalOpen={reportModalState.ctBrain}
          />
        )}
        {reportModalState.hrctChestCovid && (
          <HrctChestCovid
            regenerateClick={regenerateHRCTFunc}
            hideModal={hideModalCTBrain}
            id={reportModalState.id}
            content={reportModalState.content}
            isModalOpen={reportModalState.hrctChestCovid}
          />
        )}
        {reportModalState.mriSpine && (
          <MRISpine
            regenerateClick={regenerateHRCTFunc}
            hideModal={hideModalCT}
            id={reportModalState.id}
            content={reportModalState.content}
            isModalOpen={reportModalState.mriSpine}
          />
        )}

        {/* {showToast.show && <Toast state={showToast.state} text={showToast.message} />} */}
        {showReturnStudyModal && (
          <ReturnStudyModal
            setShowReturnStudyModal={setShowReturnStudyModal}
            showReturnStudyModal={showReturnStudyModal}
          />
        )}
        <ReportSnapshotRecoveryModal
          show={snapshotRecoveryModalOpen}
          draftTabs={snapshotDraftPatientReports}
          officialTabs={snapshotOfficialPatientReports}
          onAccept={handleSnapshotRecoveryAccept}
          onClose={handleSnapshotRecoveryClose}
          onReject={handleSnapshotRecoveryReject}
        />
        {showForceReinferenceModal && (
          <ForceReinference
            keyImages={props.keyImages}
            annotationData={props.annotationData}
            annotationDataAI={props.annotationDataAI}
            allTextToolAnnotation={props.allTextToolAnnotation}
            activeViewportIndex={props.activeViewportIndex}
          />
        )}
        {showAddendumDisclaimerModal && showConfirmModal && (
          <AddendumDisclaimerModal
            keyImages={props.keyImages}
            annotationData={props.annotationData}
            annotationDataAI={props.annotationDataAI}
            allTextToolAnnotation={props.allTextToolAnnotation}
            activeViewportIndex={props.activeViewportIndex}
          />
        )}
        <div
          className="report-container"
          style={{
            overflow: props.isStudyList ? "hidden" : "unset",
            position: "relative",
            ...(isExternalLLM
              ? { display: "flex", flexDirection: "column", height: "100%" }
              : props.isStudyList
                ? { height: "auto" }
                : {}),
          }}
        >
          {/* Loading overlay */}
          <div
            style={{
              position: "absolute",
              height: "100%",
              width: "100%",
              zIndex: 1000,
              justifyContent: "center",
              alignItems: "center",
              display: allReportTemplatesLoading ? "flex" : "none",
            }}
          >
            <Spinner
              size={SpinnerSize.small}
              styles={customSpinnerStyle}
              label="Loading..."
            />
          </div>

          <div
            className="report-container"
            style={{
              filter: allReportTemplatesLoading ? "blur(2px)" : "unset",
              // In external-LLM mode, this inner container also needs to be a flex column
              ...(isExternalLLM && {
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
              }),
              // NEW (FireFox report-panel gap fix): in the normal viewer report (non-LLM,
              // non-studylist) also make this a full-height flex column so the editor
              // area below can flex-fill the panel. Without a definite-height parent the
              // editor's calc()/% height resolved against an indefinite box and Firefox
              // collapsed it, leaving ~180px dead space by the footer (Chrome filled it).
              ...(!isExternalLLM &&
                !props.isStudyList &&
                !(enableLlmReport && isLLMAgentPresent(props.modality, studyDescription)) && {
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflow: "hidden",
                }),
            }}
          >
            {/* ── Top chrome ── */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                flexShrink: 0,
              }}
            >
              {enableLlmReport &&
                isLLMAgentPresent(props.modality, studyDescription) && (
                  <>
                    <LLMTopBar isChild={isChildStudyReportState} />
                    {/* Added prop for agentic tab fistulogram  */}
                    <LLMAddendumTabs readOnly={props.readOnly} />  
                  </>
                )}

              {!(
                enableLlmReport &&
                isLLMAgentPresent(props.modality, studyDescription)
              ) && (
                <>
                  <LockWarning
                    assignmentData={
                      props.assignmentData != undefined
                        ? props.assignmentData
                        : null
                    }
                    isStudyList={props.isStudyList}
                  />
                  <RadioDropdownSection />
                  <AddTab
                    isStudyList={props.isStudyList}
                    isChild={
                      props.isStudyList ? false : checkForParentOrChild()
                    }
                  />
                </>
              )}
            </div>

            {/* ── Editor area: fixed pixel height from measurement ── */}
            <div
              style={{
                ...(!(
                  enableLlmReport &&
                  isLLMAgentPresent(props.modality, studyDescription)
                ) && { borderBottom: "1px solid #A5A5A5" }),
                paddingBottom:
                  enableLlmReport &&
                  isLLMAgentPresent(props.modality, studyDescription)
                    ? "0"
                    : "5px",
                // NEW (FF report-panel gap fix): in the normal viewer report the editor
                // area flex-fills the column instead of a fixed calc height (which Firefox
                // mis-resolved -> ~180px dead strip). LLM / studylist paths keep editorHeight.
                // OLD: height: editorHeight,
                ...(!isExternalLLM &&
                !props.isStudyList &&
                !(enableLlmReport && isLLMAgentPresent(props.modality, studyDescription))
                  ? { flex: "1 1 auto", minHeight: 0 }
                  : { height: editorHeight }),
                overflow: "hidden",
                boxSizing: "border-box",
                ...(!(
                  enableLlmReport &&
                  isLLMAgentPresent(props.modality, studyDescription)
                ) && { paddingLeft: "22px" }),
              }}
            >
              {editorName != "" && editorName == "ckeditor" ? (
                <CustomCKEditor
                  modality={props.modality}
                  showPathologyOptions={props.showPathologyOptions}
                  customHeight={props.customHeight}
                />
              ) : (
                <CustomEditor
                  modality={props.modality}
                  showPathologyOptions={props.showPathologyOptions}
                  customHeight={props.customHeight}
                  isStudyList={props.isStudyList}
                  isChild={props.isStudyList ? false : checkForParentOrChild()}
                  editorHeight={editorHeight}
                />
              )}
            </div>

            {/* ── ChatInput + InstructionStack ── */}
            {isAgenticTab &&
              activeTab === 1 &&
              !disableEditReport &&
              enableLlmReport &&
              isLLMAgentPresent(props.modality, studyDescription) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                    height: isInstructionCollapsed ? "auto" : "128px",
                    backgroundColor: "#d9d9d9",
                    borderTop: "1px solid #ccc",
                    borderBottom: "1px solid #ccc",
                    padding: "0 5px",
                    boxSizing: "border-box",
                    marginInline: isWidget ? "0.3%" : "1.3%"
                  }}
                >
                {!(isexternalStudyReport && studyReportPdfSelected) && (
                  <div style={{ flexShrink: 0, paddingTop: "5px" }}>
                    <ChatInput onHeightChange={setChatInputHeight} reportMenu={props.reportMenu} />
                  </div>
                )}

                  <div
                    style={{
                      flex: isInstructionCollapsed ? "none" : 1,
                      overflow: "auto",
                      marginTop: isInstructionCollapsed ? "0" : "4px",
                    }}
                  >
                    <InstructionStack
                      instructions={userPrompts}
                      isCollapsed={isInstructionCollapsed}
                      onToggle={() => setIsInstructionCollapsed(!isInstructionCollapsed)}
                    />
                  </div>
                </div>
              )}

            {/* ── Action bar — always at bottom ── */}
            <div style={{ flexShrink: 0 }}>
              <AlertSubmitSection
                qcData={props.qcData}
                isStudyList={props.isStudyList}
                keyImages={props.keyImages}
                annotationData={props.annotationData}
                annotationDataAI={props.annotationDataAI}
                activeViewportIndex={props.activeViewportIndex}
                allTextToolAnnotation={props.allTextToolAnnotation}
                patientName={props.patientName}
                patientId={props.patientId}
                modality={props.modality}
                extReportReived={props.extReportReived}
                assignmentData={
                  props.assignmentData != undefined ? props.assignmentData : null
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewReport;
